// Small pieces the Asisten AI screen shares: the FAQ icon set from
// docs/contract.md section 8.1, the message limit, the Buat Dokumen hint,
// and a media query hook for the side panel versus drawer switch.
import { useEffect, useState } from "react";
import type { GlyphName } from "../ui/icons";

/** docs/contract.md 8.3: a typed message is 1 to 4000 characters. */
export const MESSAGE_MAX = 4000;
/** The counter shows from here, so the limit never arrives as a surprise. */
export const COUNTER_FROM = 3600;

const FAQ_ICONS: Record<string, GlyphName> = {
  chat: "messages",
  document: "documentText",
  calculator: "calculator",
  scale: "judge",
  checklist: "taskSquare",
  calendar: "calendar",
  building: "building",
  shield: "shieldTick",
  book: "book",
  search: "searchNormal",
};

/** The glyph for an FAQ icon name; unknown names fall back to chat. */
export function faqGlyph(name: string | null | undefined): GlyphName {
  return (name && FAQ_ICONS[name]) || "messages";
}

/**
 * A conversation title from its first message, as the API makes it: one
 * line, at most `max` characters, cut on a word boundary with an ellipsis.
 */
export function shortenTitle(text: string, max = 60): string {
  const oneLine = text.replace(/\s+/g, " ").trim();
  const chars = Array.from(oneLine);
  if (chars.length <= max) return oneLine;
  const head = chars.slice(0, max - 1).join("");
  const space = head.lastIndexOf(" ");
  const cut = space >= Math.floor(max / 3) ? head.slice(0, space) : head;
  return `${cut.replace(/[\s.,;:!?-]+$/u, "")}…`;
}

/** The drafting example (contract section 10), offered only to roles that can generate: a government one. */
export const DRAFT_EXAMPLE = "Buatkan KAK pengadaan laptop 20 unit untuk Sekretariat";
/** A Penyedia's drafting example: its own offer documents, never a government KAK. */
export const PENYEDIA_DRAFT_EXAMPLE = "Buatkan surat penawaran untuk paket pengadaan laptop 20 unit";

/** The drafting example for a role. */
export function draftExampleFor(role: string | null | undefined): string {
  return role === "penyedia" ? PENYEDIA_DRAFT_EXAMPLE : DRAFT_EXAMPLE;
}

/** True when an answer sends the user to drafting a document. */
export function pointsToDrafting(content: string): boolean {
  return /buat dokumen/i.test(content);
}

export function useMedia(query: string): boolean {
  const read = () => typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(query).matches;
  const [matches, setMatches] = useState(read);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, [query]);
  return matches;
}
